import { Controller } from "@hotwired/stimulus"

// Row icons, mirroring icon_helper's outline set (check-circle /
// exclamation-circle / document-text) — the row has to swap the SHAPE when a
// document lands, not just recolour whatever was there before (Phelipe
// 2026-08-06: an accepted COI kept the "!" circle and merely turned green).
const SVG_ATTRS = 'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"'
// Solid filled badges for the states that mean something, a neutral outline
// for an empty slot — must match _doc_row.html.erb exactly, because a row can
// be rendered by either one.
const GLYPH = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" aria-hidden="true"'
const badge = (bg, path) => `<span class="w-5 h-5 rounded-full ${bg} flex items-center justify-center">` +
  `<svg class="w-3 h-3 text-white" ${GLYPH}><path stroke-linecap="round" stroke-linejoin="round" d="${path}"/></svg></span>`
const ICONS = {
  valid: badge("bg-green-600", "m4.5 12.75 6 6 9-13.5"),
  exception: badge("bg-amber-500", "M12 5.25v9m0 4.5h.01"),
  missing: `<svg ${SVG_ATTRS} class="w-5 h-5 text-gray-400"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>`
}

// Public subcontractor set-up page (Larry 2026-08-06). One file per document
// slot: presign → PUT straight to S3 → register, then the server's AI check
// answers with either "accepted" or a plain-English reason to try again.
// Nothing here ever reloads the page — the row rewrites itself in place.
export default class extends Controller {
  static targets = [
    "row", "icon", "status", "error", "uploadLabel",
    "exceptionModal", "exceptionReason", "exceptionError", "exceptionDocLabel",
    "delegateModal", "delegateError", "delegateRowsInput", "delegateList", "delegateRows",
    "delegateSuggestions",
    "finishButton", "finishHint", "finishError",
    "removeFileBtn", "removeFileModal", "removeFileLabel", "viewLink", "requiredCount",
    "requiredCountText", "requiredCountIcon", "removeExceptionBtn"
  ]
  static values = {
    presignUrl: String, uploadUrl: String, exceptionUrl: String,
    naUrl: String, delegateUrl: String, finishUrl: String, companyName: String,
    removeFileUrl: String, removeExceptionUrl: String
  }

  connect() {
    this.activeRow = null
    this._refreshFinish()
  }

  // ── Upload ────────────────────────────────────────────────────────────
  async filePicked(event) {
    const input = event.currentTarget
    const file = input.files && input.files[0]
    if (!file) return

    const row = input.closest("[data-doc-id]")
    input.value = ""
    this._clearError(row)
    this._setCheckingStatus(row)
    this._setBusy(row, true)

    try {
      const presign = await this._post(this.presignUrlValue, {
        files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }]
      })
      if (!presign.ok) throw new Error(presign.body.error || "That file couldn't be prepared for upload.")

      const slot = presign.body.uploads[0]
      const put = await fetch(slot.upload_url, {
        method: "PUT",
        headers: { "Content-Type": slot.content_type },
        body: file
      })
      if (!put.ok) throw new Error("The upload didn't finish — try again.")

      const registered = await this._post(this._docUrl(this.uploadUrlValue, row), {
        cache_name: slot.cache_name, filename: file.name
      })
      const data = registered.body || {}
      if (!registered.ok && data.error) throw new Error(data.error)

      if (data.ok) {
        this._applyDoc(row, data)
      } else {
        // Rejected or unreadable — the row stays actionable and says why.
        this._setStatus(row, "Not accepted", "text-red-600")
        this._setIcon(row, "missing")
        this._showError(row, (data.issues || []).join(" ") || "That document wasn't accepted.")
      }
    } catch (error) {
      this._setStatus(row, "Needed", "text-gray-500")
      this._setIcon(row, "missing")
      this._showError(row, error.message)
    } finally {
      this._setBusy(row, false)
    }
  }

  // ── Exception ─────────────────────────────────────────────────────────
  openException(event) {
    this.activeRow = event.currentTarget.closest("[data-doc-id]")
    this.exceptionReasonTarget.value = ""
    this.exceptionErrorTarget.classList.add("hidden")
    this.exceptionDocLabelTarget.textContent = this.activeRow.dataset.label || ""
    this.exceptionModalTarget.classList.remove("hidden")
    this.exceptionReasonTarget.focus()
  }

  hideException() {
    this.exceptionModalTarget.classList.add("hidden")
  }

  async submitException() {
    if (!this.activeRow) return
    const reason = this.exceptionReasonTarget.value.trim()
    if (!reason) {
      this._modalError(this.exceptionErrorTarget, "Add a short reason first.")
      return
    }

    const response = await this._post(this._docUrl(this.exceptionUrlValue, this.activeRow), { reason })
    if (!response.ok) {
      this._modalError(this.exceptionErrorTarget, response.body.error || "That couldn't be filed.")
      return
    }

    // Same shape the server renders: what they wrote, then a line making clear
    // nobody is waiting on them right now.
    this._setExceptionStatus(this.activeRow, reason)
    this._setIcon(this.activeRow, "exception")
    this._clearError(this.activeRow)
    this.activeRow.querySelector("[data-vendor-docs-target='removeExceptionBtn']")?.classList.remove("hidden")
    this.hideException()
    this._refreshFinish()
  }

  // Withdraw an exception — they found the document after all.
  async removeException(event) {
    const row = event.currentTarget.closest("[data-doc-id]")
    const response = await this._send(this._docUrl(this.removeExceptionUrlValue, row), "DELETE", {})
    if (!response.ok) {
      this._showError(row, response.body.error || "That couldn't be removed.")
      return
    }

    const data = response.body || {}
    row.querySelector("[data-vendor-docs-target='removeExceptionBtn']")?.classList.add("hidden")
    if (data.satisfied) {
      // A file was still sitting underneath it.
      this._setStatus(row, data.expires_on ? `Valid through ${data.expires_on}` : "On file", "text-gray-500")
      this._setIcon(row, "valid")
    } else {
      this._setStatus(row, row.dataset.required === "true" ? "Needed" : "Optional", "text-gray-500")
      this._setIcon(row, "missing")
    }
    this._clearError(row)
  }

  // AI is reading the file right now — purple with a spinner, the same signal
  // the rest of the app uses for AI work (Phelipe 2026-08-06).
  _setCheckingStatus(row) {
    const el = row.querySelector("[data-vendor-docs-target='status']")
    if (!el) return

    el.className = "text-[13px] text-purple-700 mt-0.5 inline-flex items-center gap-1.5"
    el.innerHTML = `
      <svg class="animate-spin h-4 w-4 text-purple-600 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
      <span>Checking the document…</span>`
  }

  _setExceptionStatus(row, reason) {
    const el = row.querySelector("[data-vendor-docs-target='status']")
    if (!el) return

    const company = this.companyNameValue || "The contractor"
    el.className = "text-[13px] text-amber-700 mt-0.5"
    el.textContent = `Exception filed — “${reason}”`
    const note = document.createElement("div")
    note.className = "text-gray-400 mt-0.5"
    note.textContent = `${company} reviews this after you press Continue — nothing to wait for now.`
    el.appendChild(note)
  }

  // ── Not applicable ────────────────────────────────────────────────────
  async markNa(event) {
    const row = event.currentTarget.closest("[data-doc-id]")
    const response = await this._post(this._docUrl(this.naUrlValue, row), {})
    if (!response.ok) {
      this._showError(row, response.body.error || "That couldn't be marked not applicable.")
      return
    }

    this._setStatus(row, "Marked not applicable", "text-gray-500")
    this._setIcon(row, "valid")
    this._clearError(row)
    this._refreshFinish()
  }

  // ── Remove an uploaded file ───────────────────────────────────────────
  // They uploaded something, then realised they don't have it after all and
  // want the exception path instead.
  confirmRemoveFile(event) {
    this.activeRow = event.currentTarget.closest("[data-doc-id]")
    if (this.hasRemoveFileLabelTarget) {
      this.removeFileLabelTarget.textContent = this.activeRow.dataset.label || ""
    }
    this.removeFileModalTarget.classList.remove("hidden")
  }

  hideRemoveFile() {
    this.removeFileModalTarget.classList.add("hidden")
  }

  async removeFile() {
    const row = this.activeRow
    if (!row) return

    const response = await this._send(this._docUrl(this.removeFileUrlValue, row), "DELETE", {})
    this.hideRemoveFile()
    if (!response.ok) {
      this._showError(row, response.body.error || "That couldn't be removed.")
      return
    }

    // Back to an empty slot: Upload, no View, no Remove.
    this._setStatus(row, row.dataset.required === "true" ? "Needed" : "Optional", "text-gray-500")
    this._setIcon(row, "missing")
    this._clearError(row)
    const label = row.querySelector("[data-vendor-docs-target='uploadLabel']")
    if (label) label.textContent = "Upload"
    row.querySelector("[data-vendor-docs-target='viewLink']")?.classList.add("hidden")
    row.querySelector("[data-vendor-docs-target='removeFileBtn']")?.classList.add("hidden")
    row.querySelector("[data-vendor-docs-target='removeExceptionBtn']")?.classList.add("hidden")
    this._refreshFinish()
  }

  // ── Hand it to colleagues ─────────────────────────────────────────────
  // As many people as they need, each with their own link.
  openDelegate() {
    this.delegateErrorTarget.classList.add("hidden")
    this.delegateRowsInputTarget.innerHTML = ""
    this.addDelegateRow()
    // Chips a cancelled attempt hid come back; anyone already shared on THIS
    // contract was left out server-side, so no bookkeeping is needed here.
    if (this.hasDelegateSuggestionsTarget) {
      this.delegateSuggestionsTarget.querySelectorAll("[data-suggestion-email]")
        .forEach((chip) => chip.classList.remove("hidden"))
    }
    this.delegateModalTarget.classList.remove("hidden")
    this.delegateRowsInputTarget.querySelector("input[data-delegate-name]")?.focus()
  }

  // A person remembered from this company's earlier requests — fills a row,
  // so they can still be renamed or removed before anything is sent.
  pickSuggestion(event) {
    const chip = event.currentTarget
    const rows = Array.from(this.delegateRowsInputTarget.querySelectorAll("[data-delegate-row]"))
    let row = rows.find((r) => !r.querySelector("input[data-delegate-email]").value.trim() &&
                               !r.querySelector("input[data-delegate-name]").value.trim())
    if (!row) {
      this.addDelegateRow()
      const all = this.delegateRowsInputTarget.querySelectorAll("[data-delegate-row]")
      row = all[all.length - 1]
    }
    row.querySelector("input[data-delegate-name]").value = chip.dataset.suggestionName || ""
    row.querySelector("input[data-delegate-email]").value = chip.dataset.suggestionEmail || ""
    chip.classList.add("hidden")
  }

  hideDelegate() {
    this.delegateModalTarget.classList.add("hidden")
  }

  addDelegateRow() {
    const row = document.createElement("div")
    row.setAttribute("data-delegate-row", "")
    row.className = "flex items-start gap-2"
    row.innerHTML = `
      <div class="flex-1 space-y-2">
        <input type="text" data-delegate-name placeholder="Their name"
               class="w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px] focus:outline-none focus:border-brand">
        <input type="email" data-delegate-email placeholder="Their email" autocomplete="off"
               class="w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px] focus:outline-none focus:border-brand">
      </div>
      <button type="button" data-action="vendor-docs#removeDelegateRow"
              class="mt-2 text-gray-400 hover:text-red-500 cursor-pointer" aria-label="Remove">&times;</button>`
    this.delegateRowsInputTarget.appendChild(row)
  }

  removeDelegateRow(event) {
    const rows = this.delegateRowsInputTarget.querySelectorAll("[data-delegate-row]")
    if (rows.length <= 1) return // always keep one

    event.currentTarget.closest("[data-delegate-row]").remove()
  }

  async submitDelegate() {
    const people = Array.from(this.delegateRowsInputTarget.querySelectorAll("[data-delegate-row]"))
      .map((row) => ({
        name: row.querySelector("input[data-delegate-name]").value.trim(),
        email: row.querySelector("input[data-delegate-email]").value.trim()
      }))
      .filter((person) => person.email)

    if (!people.length) {
      this._modalError(this.delegateErrorTarget, "Enter at least one email address.")
      return
    }

    const suggested = this.hasDelegateSuggestionsTarget
      ? Array.from(this.delegateSuggestionsTarget.querySelectorAll("[data-suggestion-email]"))
        .filter((chip) => people.some((p) => p.email.toLowerCase() === chip.dataset.suggestionEmail)).length
      : 0
    const response = await this._post(this.delegateUrlValue, { people, suggested })
    if (!response.ok) {
      this._modalError(this.delegateErrorTarget, response.body.error || "That couldn't be sent.")
      return
    }

    this.hideDelegate()
    // Reload so the "shared with" list shows exactly what the server stored.
    window.location.reload()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Helpers ───────────────────────────────────────────────────────────
  _applyDoc(row, data) {
    // Say plainly that it went through (Larry 2026-08-11: "we can add more
    // text there, saying good to go or something") — green, not gray.
    if (data.na) {
      this._setStatus(row, "Marked not applicable", "text-gray-500")
    } else if (data.expires_on) {
      this._setStatus(row, `Accepted — you're good to go. Valid through ${data.expires_on}.`, "text-green-700")
    } else {
      this._setStatus(row, "Accepted — you're good to go. On file.", "text-green-700")
    }
    this._setIcon(row, "valid")
    this._clearError(row)
    const label = row.querySelector("[data-vendor-docs-target='uploadLabel']")
    if (label) label.textContent = "Replace"
  }

  _docUrl(template, row) {
    return template.replace("DOC_ID", row.dataset.docId)
  }

  async _post(url, payload) {
    return this._send(url, "POST", payload)
  }

  async _send(url, method, payload) {
    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload)
    })
    let body = {}
    try { body = await response.json() } catch (_error) { body = {} }
    return { ok: response.ok, body }
  }

  _setStatus(row, text, className) {
    const el = row.querySelector("[data-vendor-docs-target='status']")
    if (!el) return
    el.textContent = text
    el.className = `text-[13px] mt-0.5 ${className}`
  }

  _setIcon(row, state) {
    const slot = row.querySelector("[data-vendor-docs-target='icon']")
    if (slot) slot.innerHTML = ICONS[state] || ICONS.missing
    row.dataset.state = state
    this._setRowTone(row)
    this._refreshFinish()
  }

  // Red while a REQUIRED document is still outstanding — that's what's holding
  // the contract up. Everything settled (uploaded, excepted, N/A) goes back to
  // plain; on an exception only the ICON stays orange (Phelipe 2026-08-06).
  _setRowTone(row) {
    const pending = row.dataset.required === "true" && row.dataset.state === "missing"
    row.classList.toggle("border-red-200", pending)
    row.classList.toggle("bg-red-50", pending)
    row.classList.toggle("border-gray-200", !pending)
  }

  // "2 of 3 provided" beside the required heading — a settled row is one that's
  // uploaded, excepted or marked N/A.
  _refreshRequiredCount() {
    if (!this.hasRequiredCountTarget) return

    const required = this.rowTargets.filter((row) => row.dataset.required === "true")
    const done = required.filter((row) => row.dataset.state !== "missing").length
    const total = parseInt(this.requiredCountTarget.dataset.total, 10) || required.length
    const complete = done === total

    if (this.hasRequiredCountTextTarget) {
      this.requiredCountTextTarget.textContent = `${done}/${total} completed`
    }
    // Green tick appears only once every required document is settled.
    if (this.hasRequiredCountIconTarget) {
      this.requiredCountIconTarget.classList.toggle("hidden", !complete)
    }
    this.requiredCountTarget.className = complete
      ? "inline-flex items-center gap-1.5 text-[15px] font-medium text-green-700"
      : "inline-flex items-center gap-1.5 text-[15px] font-medium text-gray-500"
  }

  // ── Continue ──────────────────────────────────────────────────────────
  // Every required row has to be settled — uploaded, excepted, or N/A —
  // before the sub can hand the contract on.
  _refreshFinish() {
    this._refreshRequiredCount()
    if (!this.hasFinishButtonTarget) return
    const blocked = this.rowTargets.filter(
      (row) => row.dataset.required === "true" && row.dataset.state === "missing"
    )
    this.finishButtonTarget.disabled = blocked.length > 0
    if (this.hasFinishHintTarget) {
      this.finishHintTarget.textContent = blocked.length > 0
        ? this.finishHintTarget.dataset.blockedText || this.finishHintTarget.textContent
        : this.finishHintTarget.dataset.readyText || this.finishHintTarget.textContent
    }
  }

  async finish() {
    if (!this.hasFinishButtonTarget || this.finishButtonTarget.disabled) return
    const btn = this.finishButtonTarget
    const original = btn.textContent
    btn.disabled = true
    btn.textContent = "…"
    if (this.hasFinishErrorTarget) this.finishErrorTarget.classList.add("hidden")
    try {
      const response = await this._post(this.finishUrlValue, {})
      if (!response.ok) throw new Error(response.body.error || "")
      window.location.reload()
    } catch (err) {
      btn.disabled = false
      btn.textContent = original
      if (this.hasFinishErrorTarget) {
        this.finishErrorTarget.textContent = err.message || "Something went wrong — please try again."
        this.finishErrorTarget.classList.remove("hidden")
      }
    }
  }

  _setBusy(row, busy) {
    row.querySelectorAll("input[type=file], button").forEach((el) => { el.disabled = busy })
    row.classList.toggle("opacity-60", busy)
  }

  _showError(row, message) {
    const el = row.querySelector("[data-vendor-docs-target='error']")
    if (!el) return
    el.textContent = message
    el.classList.remove("hidden")
  }

  _clearError(row) {
    const el = row.querySelector("[data-vendor-docs-target='error']")
    if (el) el.classList.add("hidden")
  }

  _modalError(target, message) {
    target.textContent = message
    target.classList.remove("hidden")
  }
}
